/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component, HostListener, Inject, Input, OnChanges, OnInit,
} from '@angular/core';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { ButtonSize, ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MenuItem } from '../../menus/types';
import { availableLangs } from '../../exports';
import { TranslationService } from '../../../common/translation.service';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';

interface PageState {
  canEdit: boolean;
  isAuthenticated: boolean;
  isResumeTabSelected: boolean;
  isTemplate: boolean;
  type: string;
  viewType: string;
}

interface Commands {
  showShareResumeModal: Function;
  showSharePortfolioModal: Function;
  copyPageAsTemplate: Function;
  copyContentAsTemplate: Function;
}

const loader = availableLangs.reduce((acc, lang) => {
  acc[lang] = () => import(`~/resources/translations/locale-${lang}.json`);
  return acc;
}, {});

/**
 * The components and layout in this component need to be rendered on an AJS page.
 * Angular's ng-template syntax does not work in the AngularJS context, so we need
 * to use this component to render the menus on the AJS page.
 *
 * This should be a stateless component that simply passes data from AJS to Angular.
 */
@Component({
  selector: 'bulb-composite-view-menus',
  templateUrl: './composite-view-menus.component.html',
  styleUrls: ['./composite-view-menus.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: {
        scope: 'ajs',
        loader,
      },
    },
  ],
  standalone: false,
})
export class CompositeViewMenusComponent implements OnChanges, OnInit {

  @Input() pageState: PageState;

  @Input() compositeUnit: any;

  @Input() unitId: string;

  @Input() optionsMenuItems: Array<MenuItem>;

  @Input() createMenuItems: Array<MenuItem>;

  @Input() commands: Commands;

  @Input() showDotOnCreate: boolean;

  @Input() currentUser: any;

  public buttonStyle: ButtonStyle = ButtonStyle.PRIMARY_2;

  public buttonSize: ButtonSize = ButtonSize.LARGE;

  public showResumeShareButton: boolean;

  public showShareMenu: boolean;

  public showSubmitButton: boolean;

  public showCreateMenu: boolean;

  public showSharePortfolioButton: boolean;

  public showCopyCollectionButton: boolean;

  public copyCollectionItems: Array<MenuItem>;

  /**
   * Controls visibility of the create group collection button.
   */
  public showCreateGroupCollectionButton: boolean;

  /**
   * Stores the name entered for the new group collection.
   */
  groupCollectionName: string;

  isOrgMember: boolean;

  constructor(
    private $translate: TranslationService,
    private aeroDialogService: AeroDialogService,
    private httpClient: HttpClient,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {
  }

  ngOnInit(): void {
    this.copyCollectionItems = [{
      id: 'useTemplate',
      label: this.$translate.translateInstant('copy_collection'),
      icon: 'paper-plane',
      command: ($event) => {
        this.commands.copyPageAsTemplate($event);
      },
      tooltipLabel: this.$translate.translateInstant('copy_collection_info'),
    },
    {
      id: 'copyContents',
      label: this.$translate.translateInstant('copy_contents'),
      icon: 'collection',
      command: ($event) => {
        this.commands.copyContentAsTemplate($event);
      },
      tooltipLabel: this.$translate.translateInstant('copy_contents_info'),
    },
    ];
  }

  ngOnChanges(model) {
    if (model.pageState) {
      const { isAuthenticated, canEdit, isTemplate } = model.pageState.currentValue;
      const isProfile = !model.pageState.currentValue.isResumeTabSelected
        && model.pageState.currentValue.type === 'profile';
      const isResume = model.pageState.currentValue.isResumeTabSelected
        && model.pageState.currentValue.type === 'profile';
      const isCollection = model.pageState.currentValue.type === 'collection';
      const isArchive = model.pageState.currentValue.viewType === 'archive';

      this.isOrgMember = this.currentUser.groupMemberships.findIndex((org) => org.type === 'ORGANIZATION') !== -1;
      this.showResumeShareButton = canEdit && isResume;
      this.showShareMenu = isAuthenticated && canEdit && isCollection;
      this.showSubmitButton = isAuthenticated && canEdit && isCollection;
      this.showCreateMenu = isAuthenticated && canEdit && (isCollection || isProfile);
      this.showSharePortfolioButton = isAuthenticated && canEdit && isProfile
        && (this.isOrgMember ? this.currentUser.features.SharingFeature.privateLinks : true);
      this.showCopyCollectionButton = isAuthenticated && isCollection && isTemplate && !canEdit && !isArchive;
      this.showCreateGroupCollectionButton = isAuthenticated && model.pageState.currentValue.viewType === 'group'
        && this.isGroupMember();
    }
  }

  /**
   * Opens dialog to create a group collection and enables submit only for valid input.
   * Creates the collection via API and refreshes the view on success.
   */
  createGroupCollection() {
    this.aeroDialogService.showDialog({
      title: this.$translate.translateInstant('create_group_collection'),
      icon: 'fa fa-exclamation-triangle',
      iconClass: 'aero-highlight',
      showInputField: true,
      primaryDisabled: true,
      styleClass: 'create-collection',
      dialogInput: {
        inputLabel: this.$translate.translateInstant('assetlibrary_google_photos_name'),
        onInputChange: (value) => {
          this.groupCollectionName = value;
          const dialog = document.querySelector('.create-collection');
          const submitBtn = dialog.querySelector('.aero-dialog-primary-button button') as HTMLButtonElement;
          if (this.groupCollectionName === '') {
            submitBtn.disabled = true;
            submitBtn.classList.add('p-disabled'); // PrimeNG visual state
          } else {
            submitBtn.disabled = false;
            submitBtn.classList.remove('p-disabled');
          }
        },
      },
      onPrimary: () => {
        const params = new HttpParams()
          .set('name', this.groupCollectionName);

        this.httpClient.post(
          `addGroupCollection/${this.compositeUnit.id}`,
          null,
          { params },
        ).subscribe({
          next: () => window.dispatchEvent(new CustomEvent('refreshView')),
          error: (err) => this.bulbErrorService.showError(null, err),
        });
      },
    });
  }

  @HostListener('click', ['$event'])
  public toggle(/* event: Event */): void {
    if (this.showDotOnCreate) {
      this.showDotOnCreate = !this.showDotOnCreate;
    }
  }

  /**
   * Returns true if current user exists in composite unit members list.
   */
  isGroupMember(): boolean {
    return !!this.compositeUnit?.members?.some(
      (member) => member?.accountId === this.currentUser?.id,
    );
  }
}
